import { Box } from '@mui/material';
import DatePicker from 'rsuite/DatePicker';
import { CustomProvider } from 'rsuite';
import plPL from 'rsuite/locales/pl_PL';
import enUS from 'rsuite/locales/en_US';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { isAfter, isValid, startOfDay } from 'date-fns';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useInput, useLocaleState } from 'react-admin';
import { parseDateValue, toDotDateString, toIsoDateString } from '@/helpers/date/dateValue';

const formatDateForInput = value => {
    const date = parseDateValue(value);
    return date ? toDotDateString(date) : '';
};

const parseDateForApi = value => {
    if (!value) return null;
    const date = parseDateValue(value);
    return date ? toIsoDateString(date) : value;
};

const isLaterThanToday = date => isValid(date) && isAfter(startOfDay(date), startOfDay(new Date()));

export const isDateAfterToday = value => {
    const date = parseDateValue(value);
    return !!date && isLaterThanToday(date);
};

const isDateInputInvalid = (date, maxDate) => {
    if (!date) return false;
    if (!isValid(date)) return true;
    if (maxDate === 'today' && isLaterThanToday(date)) return true;
    return false;
};

const DatePickerInput = ({ value, onChange, label, placement = 'bottomStart', className, disabled, maxDate = null, onValidityChange }) => {
    const containerRef = useRef(null);
    const [currentLocale] = useLocaleState();
    const [invalidDraft, setInvalidDraft] = useState(null);
    const isPolishLocale = currentLocale?.toLowerCase().startsWith('pl');
    const pickerLocale = isPolishLocale ? plPL : enUS;

    const getContainer = useCallback(() => containerRef.current ?? document.body, []);

    const shouldDisableDate = useMemo(() => {
        if (maxDate !== 'today') {
            return undefined;
        }
        return date => isLaterThanToday(date);
    }, [maxDate]);

    useEffect(() => {
        setInvalidDraft(null);
        onValidityChange?.(true);
    }, [value, onValidityChange]);

    const dateValue = invalidDraft ?? parseDateValue(value);
    const hasError = !!invalidDraft;

    const handleDateChange = useCallback(
        date => {
            if (!date) {
                setInvalidDraft(null);
                onValidityChange?.(true);
                onChange?.('');
                return;
            }

            // Reject future / invalid typed dates — do not propagate to filters.
            if (isDateInputInvalid(date, maxDate)) {
                setInvalidDraft(date);
                onValidityChange?.(false);
                return;
            }

            setInvalidDraft(null);
            onValidityChange?.(true);
            onChange?.(toDotDateString(date));
        },
        [maxDate, onChange, onValidityChange],
    );

    const handlePickerState = useCallback(
        isOpen => {
            const root = containerRef.current || document;
            const pickerElements = root.querySelectorAll('.rs-picker-toggle, .rs-input-group.rs-input-group-inside');
            pickerElements.forEach(pickerElement => {
                if (isOpen && !hasError) {
                    pickerElement.style.border = `2px solid ${Palette.gray.gray900}`;
                    pickerElement.style.outline = `2px solid ${Palette.yellow.yellow200}`;
                } else {
                    pickerElement.style.border = '';
                    pickerElement.style.outline = '';
                }
            });

            if (!isOpen && invalidDraft) {
                setInvalidDraft(null);
                onValidityChange?.(true);
            }
        },
        [hasError, invalidDraft, onValidityChange],
    );

    return (
        <Box
            ref={containerRef}
            className={clsx('date-picker', className, hasError && 'date-picker--error')}
            sx={{
                position: 'relative',
                ...(hasError && {
                    '& .rs-picker-toggle, & .rs-input-group.rs-input-group-inside': {
                        border: `2px solid ${Palette.red.red500} !important`,
                        outline: 'none !important',
                    },
                }),
            }}
        >
            <span className='date-picker-label'>{label}</span>
            <CustomProvider locale={pickerLocale}>
                <DatePicker
                    value={dateValue}
                    onChange={handleDateChange}
                    onOpen={() => handlePickerState(true)}
                    onClose={() => handlePickerState(false)}
                    format='dd.MM.yyyy'
                    placement={placement}
                    container={getContainer}
                    disabled={disabled}
                    shouldDisableDate={shouldDisableDate}
                />
            </CustomProvider>
        </Box>
    );
};

DatePickerInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    onChange: PropTypes.func,
    label: PropTypes.string,
    placement: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    maxDate: PropTypes.oneOf(['today']),
    onValidityChange: PropTypes.func,
};

const DateInputWithSource = ({
    source,
    name,
    label,
    placement = 'bottomStart',
    className,
    defaultValue,
    validate,
    parse,
    format,
    onChange,
    onBlur,
    resource,
    disabled,
    readOnly,
    maxDate,
    onValidityChange,
    ...rest
}) => {
    const { field } = useInput({
        source,
        name,
        defaultValue,
        validate,
        parse: parse ?? parseDateForApi,
        format: format ?? formatDateForInput,
        onChange,
        onBlur,
        resource,
        disabled,
        readOnly,
        ...rest,
    });

    return (
        <DatePickerInput
            className={className}
            label={label}
            placement={placement}
            value={field.value}
            onChange={field.onChange}
            disabled={disabled || readOnly}
            maxDate={maxDate}
            onValidityChange={onValidityChange}
        />
    );
};

DateInputWithSource.propTypes = {
    source: PropTypes.string.isRequired,
    name: PropTypes.string,
    label: PropTypes.string,
    placement: PropTypes.string,
    className: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    validate: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    parse: PropTypes.func,
    format: PropTypes.func,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    resource: PropTypes.string,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    maxDate: PropTypes.oneOf(['today']),
    onValidityChange: PropTypes.func,
};

export const DateInput = ({ placement = 'bottomStart', ...props }) => {
    if (props.source) {
        return <DateInputWithSource placement={placement} {...props} />;
    }

    return <DatePickerInput placement={placement} {...props} />;
};

DateInput.propTypes = {
    source: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    label: PropTypes.string,
    placement: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    maxDate: PropTypes.oneOf(['today']),
    onValidityChange: PropTypes.func,
};
